import styled from '@emotion/styled'
import { BID_ADJUSTMENT_DECIMALS, BidderSeat, displayBidderSeatName, productOfBidderSeatType, roundBidAdjustment } from '@pubstack/common/src/bidder-seat'
import { useQueryClient } from '@tanstack/react-query'
import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useAdUnits as usePbsAdUnits } from '~/api/pbs/ad-units.api'
import { BIDDER_SEATS_QUERY_KEYS, useUpdateBidderSeat } from '~/api/bidder-seats.api'
import { useCatalogBidder } from '~/api/catalog.api'
import { useScopePermissions } from '~/auth/useScopePermissions'
import { NoDataSpreadsheet } from '~/components/_styled/spreadsheet'
import { Title_H2 } from '~/components/_styled/titles'
import Button from '~/components/Button'
import { ConfirmationModal } from '~/components/ConfirmationModal'
import { Flyout } from '~/components/Flyout'
import { Icon } from '~/components/Icon'
import { Input } from '~/components/Input'
import { useGlobalFlyout } from '~/components/layout/GlobalFlyout'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { label, prefix, TrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { Skeleton } from '~/components/Skeleton'
import { ToggleInput } from '~/components/Toggle'
import { Tooltip } from '~/components/Tooltip'
import { useCloseGuard } from '~/hooks/useCloseGuard'
import { PATH_BY_PRODUCT } from '~/hooks/useProduct'
import { BidderMapping, BidderMappingHandle } from './BidderMapping'
import { MappableAdUnit } from './mappingInventory'
import { SeatTypeChip } from '../SeatTypeChip'

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

/** `Flyout.Body` is already a bounded, scrollable grid row — this just turns its height into a budget the two sections below can split, instead of leaving it unclaimed under a short mapping table. */
const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  height: 100%;
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const MappingSection = styled(Section)`
  flex: 1;
  min-height: 0;
`

const BidAdjustmentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`

/**
 * Fixed width of its own rather than a `:last-child` rule on the row — the toggle is the row's only
 * child when off, and a positional selector would size it the same as the input once it hides.
 *
 * Stays mounted and merely invisible when the toggle is off, rather than unmounting: the input is
 * taller than the toggle, so removing it from the DOM shrank the row and shifted the mapping section
 * beneath it on every toggle — a self-inflicted layout shift a fixed-height row avoids for free.
 */
const BidAdjustmentInput = styled.div<{ $visible: boolean }>`
  width: 160px;
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
`

type BidAdjustmentFormModel = {
  bidAdjustment: {
    enable: boolean
    adjustment: number
  }
}

/** Same shape `MappingOptionsFlyout.buildFormModel` reads off a seat, narrowed to the one field this flyout edits. */
const buildBidAdjustmentFormModel = (bidderSeat: BidderSeat): BidAdjustmentFormModel => ({
  bidAdjustment: {
    enable: bidderSeat.bidAdjustment.enabled,
    adjustment: Number(((bidderSeat.bidAdjustment.value ?? 0) * 100).toFixed(BID_ADJUSTMENT_DECIMALS - 2)),
  },
})

type Props = {
  bidderSeat: BidderSeat
}

/**
 * Spark Demand and Spark for Pubs (the Spark team's half) rows open here instead of navigating to
 * `BidderSeatPage` — both are staff-only, scope-level seats with nothing on that page worth a screen of
 * its own beyond bid adjustment and the mapping table, so a flyout over the list is faster to work
 * through. Every other tab keeps the full page: it carries per-media filters and CSV import/export a
 * flyout has no room for, and Edit here opens it for the same reason.
 *
 * Bid adjustment is its own compact toggle+input row rather than the fuller `BidAdjustment` feature
 * component (title, description, its own row for the value) — that component still fits the spacious
 * "Seat settings" tab of `MappingOptionsFlyout`, but read twice as much as this narrower flyout needs.
 *
 * One Save covers both halves: they are one edit of one seat as far as the reader is concerned, and two
 * buttons a section apart asked them to remember which of their changes each one carried.
 */
export const SeatQuickEditFlyout: FunctionComponent<Props> = ({ bidderSeat }) => {
  const { canEdit, disabledReason } = useScopePermissions()
  const queryClient = useQueryClient()
  const modal = useGlobalModal()
  const flyout = useGlobalFlyout()
  const navigate = useNavigate()
  const registerCloseGuard = useCloseGuard()
  const mappingRef = useRef<BidderMappingHandle>(null)
  // Both tabs this flyout serves hold only Node seats — see `myBidderSeatTabs.tabOfSeat`.
  const product = productOfBidderSeatType(bidderSeat.type)

  const [hasUnsavedMappings, setHasUnsavedMappings] = useState(false)
  const [hasErroredMappings, setHasErroredMappings] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const BID_ADJUSTMENT_TOGGLE_TRACKING_LABEL: `${prefix}-${label}` = `${product}-bidder-seat-quick-edit-bid-adjustment`
  const SAVE_TRACKING_LABEL: TrackingLabel = `${product}-bidder-seat-quick-edit-save`
  const EDIT_TRACKING_LABEL: TrackingLabel = `${product}-bidder-seat-quick-edit-navigate`

  const { data: catalogBidder, isLoading: isLoadingCatalogBidder } = useCatalogBidder(product, bidderSeat.bidderCode)
  // Neither tab holds the cross-inventory `node` seat, so only Node's own ad units are ever offered here.
  const { data: pbsAdUnits, isLoading: isLoadingAdUnits } = usePbsAdUnits({ enabled: true }, product === 'pbs')
  // Node's inventory only, so it says so rather than being recognised by carrying an `env`.
  const adUnits = useMemo<MappableAdUnit[]>(() => pbsAdUnits.map((adUnit) => ({ ...adUnit, product: 'pbs' as const })), [pbsAdUnits])

  const { mutateAsync: updateBidderSeat } = useUpdateBidderSeat(product, bidderSeat.id, {
    errorToast: 'Error while saving the bid adjustment',
  })

  const {
    control,
    formState: { errors, isDirty: isBidAdjustmentDirty },
    reset,
    getValues,
  } = useForm<BidAdjustmentFormModel>({
    defaultValues: buildBidAdjustmentFormModel(bidderSeat),
    mode: 'onChange',
  })
  const isBidAdjustmentEnabled = useWatch({ control, name: 'bidAdjustment.enable' })

  const hasChanges = isBidAdjustmentDirty || hasUnsavedMappings

  useEffect(() => {
    // Skipped mid-save: saving the mapping invalidates the seat, and the refetched object would reset the
    // bid-adjustment form out from under a save that has not written it yet.
    if (isSaving) return
    reset(buildBidAdjustmentFormModel(bidderSeat))
  }, [bidderSeat])

  /** Shared by the close guard and Edit — both leave this flyout with whatever has not been saved. */
  const confirmDiscard = useCallback(async () => {
    if (!hasChanges) return true
    return new Promise<boolean>((resolve) => {
      modal.open(ConfirmationModal, {
        title: 'Unsaved changes',
        message: 'You have unsaved changes. Are you sure you want to continue?',
        trackingLabel: 'common-confirm-navigation',
        onCancel: () => {
          modal.close()
          resolve(false)
        },
        onConfirm: () => {
          modal.close()
          resolve(true)
        },
      })
    })
  }, [hasChanges, modal])

  // Registered fresh on every render so the guard always reads the latest dirty state — `useCloseGuard`
  // stores the callback by reference, and a stale closure would keep answering with the state from
  // whichever render first registered it.
  useEffect(() => registerCloseGuard(confirmDiscard))

  const handleSave = async () => {
    if (!hasChanges || hasErroredMappings || isSaving) return
    setIsSaving(true)
    try {
      // Bid adjustment first: saving the mapping invalidates the seat, and letting that land before this
      // write would hand the mutation a stale seat to spread over.
      if (isBidAdjustmentDirty) {
        const value = getValues()
        await updateBidderSeat({
          ...bidderSeat,
          bidAdjustment: {
            enabled: value.bidAdjustment.enable,
            value: roundBidAdjustment(value.bidAdjustment.adjustment / 100),
          },
        })
        reset(value)
      }
      // No-ops on an empty draft, so it is safe whether or not the spreadsheet was touched.
      await mappingRef.current?.save()
      // Only on success: a thrown mutation skips this and leaves the flyout open with the toast that
      // already reported the error, and whatever did not save still in the form to retry.
      flyout.close()
    } finally {
      setIsSaving(false)
    }
  }

  /** The full seat page, for the per-media filters and CSV import/export this flyout leaves out. */
  const handleEdit = async () => {
    if (!(await confirmDiscard())) return
    // The pathname change closes the flyout on its own — see `GlobalFlyoutWrapper`.
    navigate(`${PATH_BY_PRODUCT[product]}/integrations/my-bidder-seats/${bidderSeat.id}`)
  }

  const handleMappingSaved = () => {
    queryClient.invalidateQueries({ queryKey: [product, ...BIDDER_SEATS_QUERY_KEYS, bidderSeat.id] })
  }

  return (
    <Flyout.Content>
      <Flyout.Title>
        <Header>
          {displayBidderSeatName(bidderSeat)}
          <SeatTypeChip seat={bidderSeat} />
        </Header>
      </Flyout.Title>
      <Flyout.Body>
        <Body>
          <Section>
            <Title_H2>Bid adjustment</Title_H2>
            <BidAdjustmentRow>
              <ToggleInput control={control} name="bidAdjustment.enable" disabled={!canEdit} trackingLabel={`${BID_ADJUSTMENT_TOGGLE_TRACKING_LABEL}-${isBidAdjustmentEnabled ? 'unset' : 'set'}`} />
              <BidAdjustmentInput $visible={isBidAdjustmentEnabled}>
                <Input
                  label="% of adjustment"
                  labelIsPlaceholder
                  control={control}
                  name="bidAdjustment.adjustment"
                  disabled={!canEdit || !isBidAdjustmentEnabled}
                  error={errors.bidAdjustment?.adjustment?.message}
                  type="number"
                  min={1}
                  step={0.01}
                  rules={{
                    validate: {
                      // Off skips validation outright — hidden and disabled is not a value the reader can fix.
                      min: (value) => !isBidAdjustmentEnabled || Number(value) > 0 || 'Should be greater than 0',
                    },
                    required: isBidAdjustmentEnabled,
                  }}
                  iconRight="percent"
                />
              </BidAdjustmentInput>
            </BidAdjustmentRow>
          </Section>
          <MappingSection>
            <Title_H2>Mapping</Title_H2>
            {isLoadingCatalogBidder || isLoadingAdUnits ? (
              <Skeleton bigger />
            ) : !catalogBidder ? (
              <NoDataSpreadsheet $fillHeight>
                <Icon name="night_sky" width="144px" />
                <div>Something went wrong while loading data</div>
              </NoDataSpreadsheet>
            ) : (
              <BidderMapping
                ref={mappingRef}
                bidderSeat={bidderSeat}
                catalogBidder={catalogBidder}
                adUnits={adUnits}
                platform={bidderSeat.platform ?? 'server'}
                product={product}
                onSaveSuccess={handleMappingSaved}
                disabled={!canEdit}
                hideSaveButton
                onUnsavedMappingsChange={setHasUnsavedMappings}
                onErroredMappingsChange={setHasErroredMappings}
                fillHeight
              />
            )}
          </MappingSection>
        </Body>
      </Flyout.Body>
      <Flyout.Actions>
        <Tooltip title={disabledReason} disabled={canEdit} reposition>
          <Button
            iconName={isSaving ? 'pubstack' : undefined}
            iconSpin={isSaving}
            disabled={!hasChanges || hasErroredMappings || isSaving || !canEdit}
            onClick={handleSave}
            trackingLabel={SAVE_TRACKING_LABEL}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </Tooltip>
        <Button variant="tertiary" onClick={handleEdit} trackingLabel={EDIT_TRACKING_LABEL}>
          Edit
        </Button>
      </Flyout.Actions>
    </Flyout.Content>
  )
}
